import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, RefreshCw, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { TONI, canaleOf } from "@/lib/ped";

export const PostAiPanel = ({ clientId, tema, canali, formato, testo, onApply }) => {
  const [tono, setTono] = useState("amichevole");
  const [loading, setLoading] = useState(false);
  const [varianti, setVarianti] = useState([]);
  const [error, setError] = useState("");

  const generate = async () => {
    if (!tema || tema.trim().length < 3) return toast.error("Inserisci prima il titolo / tema del post");
    if (!canali.length) return toast.error("Seleziona almeno un canale");
    setLoading(true); setError("");
    try {
      const r = await api.post("/ai/caption", { client_id: clientId, tema, canali, formato, tono, testo_attuale: testo || "" });
      setVarianti(r.data.varianti);
    } catch (e) { setError(errMsg(e, "Generazione non riuscita")); }
    finally { setLoading(false); }
  };

  return (
    <div className="rounded-2xl border border-[#0A1128]/15 bg-gradient-to-b from-[#A6DAFC] to-[#8FD0FB] p-4" data-testid="post-ai-panel">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="font-display font-extrabold text-[#0A1128] flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#E8173A]" />Assistente AI</div>
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Claude Sonnet 5.5</span>
      </div>
      <div className="flex gap-2">
        <Select value={tono} onValueChange={setTono}>
          <SelectTrigger className="rounded-xl bg-[#A6DAFC] w-[160px]" data-testid="ai-tono-select"><SelectValue /></SelectTrigger>
          <SelectContent>{TONI.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
        <Button type="button" onClick={generate} disabled={loading} className="flex-1 rounded-full bg-[#0A1128] hover:bg-[#E8173A] font-bold" data-testid="ai-generate-button">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : varianti.length ? <RefreshCw className="h-4 w-4 mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
          {loading ? "Sto scrivendo..." : varianti.length ? "Rigenera" : "Genera con AI"}
        </Button>
      </div>
      {error && <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-[#C8102E]" data-testid="ai-error">{error}</div>}
      <div className="mt-3 space-y-3 max-h-[340px] overflow-y-auto core-scroll pr-1">
        {varianti.map((v, i) => {
          const ch = canaleOf(v.canale);
          const I = ch.icon;
          return (
            <div key={`${v.canale}-${i}`} className="rounded-xl bg-[#A6DAFC] border border-slate-200 p-3" data-testid={`ai-variant-${v.canale}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="inline-flex items-center gap-1.5 text-xs font-bold" style={{ color: ch.color }}><I size={13} />{ch.label}</span>
                <Button type="button" size="sm" variant="outline" onClick={() => { onApply(v); toast.success(`Testo ${ch.label} inserito`); }} className="h-7 rounded-full text-xs font-bold" data-testid={`ai-use-${v.canale}`}>
                  <Check className="h-3.5 w-3.5 mr-1" />Usa questo testo
                </Button>
              </div>
              <div className="text-sm text-slate-700 whitespace-pre-wrap">{v.caption}</div>
              {v.hashtag && <div className="text-xs font-semibold text-[#0A1128] mt-2">{v.hashtag}</div>}
            </div>
          );
        })}
        {!varianti.length && !loading && !error && <p className="text-xs text-slate-500">Genera caption e hashtag a partire da tema, canali, formato e dati del cliente. Puoi rigenerare e modificare il testo prima di salvare.</p>}
      </div>
    </div>
  );
};
